import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    CheckCircle,
    Clock,
    XCircle,
    Box,
    IndianRupee,
    Check,
    X,
    Inbox,
    SearchX,
    Sparkles,
    Package,
} from 'lucide-react'
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from './Marketplace/MarketplaceEmptyState'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import {
    getPackageRequestAPI,
    sendPackageApprovePriceAPI,
} from '../../../Redux/ApiCalls/Dashboard/PackageAPI'
import { PackageRequest } from '../../../Data/package.dto'
import ApproveModal from '../../atoms/Dashboard/Package/ApprovePackageModal'
import RejectPackageModal from '../../atoms/Dashboard/Package/RejectPackageModal'
import PhoneSvg from '../../../assets/Icons/phone.svg'
import DateSvg from '../../../assets/Icons/dateCalendar.svg'
import { device } from '../../atoms/Utlis/media'

interface StatusConfig {
    bg: string
    color: string
    icon: React.ElementType
    label: string
}

type StatusFilter = 'all' | 'approved' | 'pending' | 'rejected'

const statusStyles: Record<PackageRequest['status'], StatusConfig> = {
    approved: {
        bg: 'rgba(49,166,106,0.12)',
        color: '#31a66a',
        icon: CheckCircle,
        label: 'Approved',
    },
    pending: {
        bg: 'rgba(240,154,62,0.15)',
        color: '#f09a3e',
        icon: Clock,
        label: 'Pending',
    },
    rejected: {
        bg: 'rgba(228,72,98,0.12)',
        color: '#e44862',
        icon: XCircle,
        label: 'Rejected',
    },
}

const PackagesRequest: React.FC = () => {
    const dispatch = useAppDispatch()
    const { packageRequest, loading } = useAppSelector((state) => state.package)

    const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('all')
    const [approveModal, setApproveModal] = useState<boolean>(false)
    const [rejectModal, setRejectModal] = useState<boolean>(false)
    const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null)

    useEffect(() => {
        dispatch(getPackageRequestAPI())
    }, [dispatch])

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const handleApprove = (price: number) => {
        if (selectedRequestId && price > 0) {
            dispatch(
                sendPackageApprovePriceAPI({
                    price: Number(price),
                    action: 'approve',
                    request_ids: [selectedRequestId],
                })
            ).then(() => {
                dispatch(getPackageRequestAPI())
            })
            setApproveModal(false)
            setSelectedRequestId(null)
        }
    }

    const handleReject = () => {
        if (selectedRequestId) {
            dispatch(
                sendPackageApprovePriceAPI({
                    action: 'reject',
                    request_ids: [selectedRequestId],
                })
            ).then(() => {
                dispatch(getPackageRequestAPI())
            })
            setRejectModal(false)
            setSelectedRequestId(null)
        }
    }

    const filteredData: PackageRequest[] =
        selectedStatus === 'all'
            ? packageRequest
            : packageRequest.filter(
                (item: PackageRequest) => item.status === selectedStatus
            )

    const stats = useMemo(() => ({
        total: packageRequest.length,
        pending: packageRequest.filter((d) => d.status === 'pending').length,
        approved: packageRequest.filter((d) => d.status === 'approved').length,
        rejected: packageRequest.filter((d) => d.status === 'rejected').length,
    }), [packageRequest])

    const statusFilters: StatusFilter[] = ['all', 'approved', 'pending', 'rejected']

    return (
        <PageContainer>
            <Hero>
                <HeroGlow />
                <HeroContent>
                    <HeroText>
                        <div className="flex flex-col">
                            <HeroBadge>
                                <Sparkles size={13} />
                                Package pricing
                            </HeroBadge>
                            <PageTitle className="linear-text">Package Price Requests</PageTitle>
                            <PageSubtitle>
                                Manage and review customer package requests — approve with a custom price or reject.
                            </PageSubtitle>
                        </div>
                    </HeroText>
                </HeroContent>
            </Hero>

            <StatsGrid>
                <WalletHero>
                    <WalletIconWrap>
                        <Package size={16} />
                    </WalletIconWrap>
                    <WalletHeroBody>
                        <WalletHeroValue>{stats.total}</WalletHeroValue>
                        <WalletHeroLabel>Total requests</WalletHeroLabel>
                    </WalletHeroBody>
                </WalletHero>

                <StatCard $tone="#f09a3e">
                    <StatIcon $tone="#f09a3e">
                        <Clock size={16} />
                    </StatIcon>
                    <StatBody>
                        <StatValue>{stats.pending}</StatValue>
                        <StatLabel>Pending</StatLabel>
                    </StatBody>
                </StatCard>

                <StatCard $tone="#31a66a">
                    <StatIcon $tone="#31a66a">
                        <CheckCircle size={16} />
                    </StatIcon>
                    <StatBody>
                        <StatValue>{stats.approved}</StatValue>
                        <StatLabel>Approved</StatLabel>
                    </StatBody>
                </StatCard>

                <StatCard $tone="#e44862">
                    <StatIcon $tone="#e44862">
                        <XCircle size={16} />
                    </StatIcon>
                    <StatBody>
                        <StatValue>{stats.rejected}</StatValue>
                        <StatLabel>Rejected</StatLabel>
                    </StatBody>
                </StatCard>
            </StatsGrid>

            <FilterRow>
                {statusFilters.map((status) => (
                    <FilterTab
                        key={status}
                        type="button"
                        $active={selectedStatus === status}
                        onClick={() => setSelectedStatus(status)}
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </FilterTab>
                ))}
            </FilterRow>

            <Panel>
                {loading ? (
                    <MarketplaceLoadingSpinner padding="60px 22px" />
                ) : filteredData.length === 0 ? (
                    <MarketplaceEmptyState
                        padding="32px 22px 48px"
                        icon={selectedStatus !== 'all' ? <SearchX size={36} strokeWidth={1.5} /> : <Inbox size={36} strokeWidth={1.5} />}
                        title={selectedStatus !== 'all' ? 'No requests found' : 'No package requests yet'}
                        description={
                            selectedStatus !== 'all'
                                ? `No ${selectedStatus} requests right now. Try another filter or view all requests.`
                                : "When customers request a package, they'll show up here for you to approve or reject."
                        }
                        actions={
                            selectedStatus !== 'all'
                                ? [{ label: 'Show all requests', onClick: () => setSelectedStatus('all'), variant: 'secondary' as const }]
                                : []
                        }
                        hint={selectedStatus === 'all' ? {
                            icon: <Sparkles size={13} />,
                            text: 'Approve with a custom price or reject pending requests',
                        } : undefined}
                    />
                ) : (
                    <RequestList>
                        {filteredData.map((request) => {
                            const statusConfig = statusStyles[request.status]
                            const StatusIcon = statusConfig.icon

                            return (
                                <RequestCard key={request.id}>
                                    <RequestBody>
                                        <RequestLeft>
                                            <RequestIndex>{request.id}</RequestIndex>
                                            <RequestDetails>
                                                <RequestName>{request.user}</RequestName>
                                                <MetaGrid>
                                                    <MetaItem>
                                                        <img src={PhoneSvg} alt="" />
                                                        <span>{request.phone}</span>
                                                    </MetaItem>
                                                    <MetaItem>
                                                        <Box size={16} color="#c94daa" />
                                                        <span>{request.package}</span>
                                                    </MetaItem>
                                                    <MetaItem>
                                                        <img src={DateSvg} alt="" />
                                                        <span>{formatDate(request.created_at)}</span>
                                                    </MetaItem>
                                                    {request.admin_price && (
                                                        <MetaItem>
                                                            <IndianRupee size={16} color="#c94daa" />
                                                            <PriceText>
                                                                ₹{parseFloat(request.admin_price).toLocaleString('en-IN')}
                                                            </PriceText>
                                                        </MetaItem>
                                                    )}
                                                </MetaGrid>
                                            </RequestDetails>
                                        </RequestLeft>
                                        <StatusBadge $bg={statusConfig.bg} $color={statusConfig.color}>
                                            <StatusIcon size={15} />
                                            {statusConfig.label}
                                        </StatusBadge>
                                    </RequestBody>

                                    {request.status === 'pending' && (
                                        <ActionBar>
                                            <ApproveBtn
                                                type="button"
                                                onClick={() => {
                                                    setSelectedRequestId(request.id)
                                                    setApproveModal(true)
                                                }}
                                            >
                                                <Check size={16} />
                                                Approve
                                            </ApproveBtn>
                                            <RejectBtn
                                                type="button"
                                                onClick={() => {
                                                    setSelectedRequestId(request.id)
                                                    setRejectModal(true)
                                                }}
                                            >
                                                <X size={16} />
                                                Reject
                                            </RejectBtn>
                                        </ActionBar>
                                    )}
                                </RequestCard>
                            )
                        })}
                    </RequestList>
                )}
            </Panel>

            {approveModal && (
                <ApproveModal
                    isOpen={approveModal}
                    onClose={() => setApproveModal(false)}
                    onSend={handleApprove}
                />
            )}
            {rejectModal && (
                <RejectPackageModal
                    isOpen={rejectModal}
                    onClose={() => setRejectModal(false)}
                    onReject={handleReject}
                />
            )}
        </PageContainer>
    )
}

export default PackagesRequest

const gradient = 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`

const HeroText = styled.div`
  min-width: 0;
`

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media ${device.laptop} { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`

const StatCard = styled.div<{ $tone: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`

const StatBody = styled.div`
  min-width: 0;
`

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`

const FilterRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  width: fit-content;
`

const FilterTab = styled.button<{ $active: boolean }>`
  padding: 8px 18px;
  border: none;
  border-radius: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(p) => (p.$active ? gradient : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'rgba(23, 23, 23, 0.62)')};
  box-shadow: ${(p) => (p.$active ? '0 4px 12px rgba(201, 77, 170, 0.25)' : 'none')};
  &:hover {
    background: ${(p) => (p.$active ? gradient : 'rgba(23, 23, 23, 0.04)')};
  }
`

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`

const RequestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px;
  overflow-y: auto;
`

const RequestCard = styled.div`
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    box-shadow: 0 8px 28px rgba(201, 77, 170, 0.1);
    transform: translateY(-1px);
  }
`

const RequestBody = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  flex-wrap: wrap;
`

const RequestLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
`

const RequestIndex = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  flex-shrink: 0;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.2) 0%, rgba(206, 86, 176, 0.2) 100%);
  color: #c94daa;
`

const RequestDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const RequestName = styled.h3`
  margin: 0 0 10px;
  font-size: 17px;
  font-weight: 800;
  color: #171717;
`

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.65);
  img { width: 16px; height: 16px; }
`

const PriceText = styled.span`
  font-weight: 700;
  background: ${gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const StatusBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  white-space: nowrap;
  flex-shrink: 0;
`

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 22px;
  background: rgba(23, 23, 23, 0.02);
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`

const ApproveBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(360deg, #059669 0%, #10b981 100%);
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.92; }
`

const RejectBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(360deg, #dc2626 0%, #ef4444 100%);
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.92; }
`
